import { useRef, useState, useEffect, useCallback } from 'react';

/**
 * IntersectionObserver hook for lazy-rendering chart cells.
 * Once visible, stays visible forever (never unmounts Recharts — reinit is expensive).
 */
export function useVisibility(rootMargin = '200px'): [(node: HTMLDivElement | null) => void, boolean] {
  const nodeRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const [visible, setVisible] = useState(false);

  const setRef = useCallback((node: HTMLDivElement | null) => {
    // Disconnect previous observer
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }

    nodeRef.current = node;

    if (visible || !node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { rootMargin },
    );

    observer.observe(node);
    observerRef.current = observer;
  }, [visible, rootMargin]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, []);

  return [setRef, visible];
}
